import * as moment from 'moment';

import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
  EventSchedule,
  EventTimeSlot,
  EventType,
  ScheduleDeletionPreviewEvent,
  getScheduleDeletionBookingWarning,
  ROLLING_ORPHAN_REASON_SINGULAR,
  ROLLING_ORPHAN_REASON_PLURAL
} from '../../../../../../../models/event-type';

import { AccountService } from '../../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../../services/event-type/event-type.service';
import { dateStringFromISO8601, timeStringFromISO8601TimeOnly } from '../../../../../../../utils';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';
import { EventScheduleRepetition, ScheduleType } from '../../../../../../../models/event-type-enums';
import { CapitanLocation } from '../../../../../../../models/location';
import { SimpleMessageModalComponent } from '../../../../../../../components/modals/simple-message-modal/simple-message-modal.component';
// tslint:disable-next-line:max-line-length
import { ScheduleOrphanedEventsModalComponent } from '../../../modals/schedule-orphaned-events-modal/schedule-orphaned-events-modal.component';

@Component({
  selector: 'app-event-type-schedules-repeating',
  templateUrl: './event-type-schedules-repeating.component.html',
  styleUrls: ['./event-type-schedules-repeating.component.css']
})
export class EventTypeSchedulesRepeatingComponent implements OnInit {

  @Input()
  eventType: EventType;

  @Input()
  locations: CapitanLocation[];

  // null indicates "all locations"
  public locationId: number | null = null;
  public hideEnded = true;

  public eventSchedules: EventSchedule[];
  // A mapping from schedule IDs to a list of timeslots for that schedule.
  public eventTimeSlots: {[key: number]: EventTimeSlot[]} = {};

  public loadingEventSchedules = false;

  public eventSchedulesNotFound = false;
  public serverError: number = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;
  public readonly timeStringFromISO8601TimeOnly = timeStringFromISO8601TimeOnly;
  public readonly EventScheduleRepetition = EventScheduleRepetition;
  public readonly ScheduleType = ScheduleType;

  constructor(
    private accountService: AccountService,
    public organizationConfigService: OrganizationConfigService,
    private eventTypeService: EventTypeService,
    private modalService: NgbModal
  ) {}

  ngOnInit() {
    this.loadEventSchedules(this.eventType.id);
  }

  /**
   * Load the event schedules for the given event type ID.
   *
   * @param eventTypeId - The API ID of the event type.
   */
  public loadEventSchedules(eventTypeId: number) {
    this.loadingEventSchedules = true;

    this.eventTypeService
      .getEventSchedules(eventTypeId, 1, 3000)
      .then(scheduleResponse => {
        this.eventSchedules = scheduleResponse.results;

        return Promise.all(
          this.eventSchedules.map(es => {
            // TODO: Support more than 100 time slots
            return this.eventTypeService
              .getEventTimeSlots(
                eventTypeId,
                es.id,
                1,
                100
              )
              .then(timeSlotResponse => {
                this.eventTimeSlots[es.id] = timeSlotResponse.results;
              });
          })
        );
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventSchedulesNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventSchedules = false; });
  }

  /**
   * Whether all days are available for the given time slot.
   */
  public allDaysAreAvailable(timeSlot: EventTimeSlot) {
    return (
      timeSlot.is_available_sunday &&
      timeSlot.is_available_monday &&
      timeSlot.is_available_tuesday &&
      timeSlot.is_available_wednesday &&
      timeSlot.is_available_thursday &&
      timeSlot.is_available_friday &&
      timeSlot.is_available_saturday
    );
  }

  /**
   * Get the given duration value in human-readable text form.
   */
  public getDurationText(durationMinutes: number): string {
    if ((durationMinutes % 60) === 0) {
      const numHours = durationMinutes / 60;
      if (numHours === 1) {
        return '1 hour';
      } else {
        return numHours.toString() + ' hours';
      }
    } else {
      return durationMinutes.toString() + ' min';
    }
  }

  /**
   * Get the set of available days in the given time slot in human-readable
   * text form.
   */
  public getTimeSlotDaysText(timeSlot: EventTimeSlot): string {
    if (this.allDaysAreAvailable(timeSlot)) {
      return 'Every day';
    }

    if (
      !timeSlot.is_available_sunday &&
      timeSlot.is_available_monday &&
      timeSlot.is_available_tuesday &&
      timeSlot.is_available_wednesday &&
      timeSlot.is_available_thursday &&
      timeSlot.is_available_friday &&
      !timeSlot.is_available_saturday
    ) {
      return 'Weekdays';
    }

    if (
      timeSlot.is_available_sunday &&
      !timeSlot.is_available_monday &&
      !timeSlot.is_available_tuesday &&
      !timeSlot.is_available_wednesday &&
      !timeSlot.is_available_thursday &&
      !timeSlot.is_available_friday &&
      timeSlot.is_available_saturday
    ) {
      return 'Weekends';
    }

    const availableDayStrings = [];
    if (timeSlot.is_available_sunday) { availableDayStrings.push('Sunday'); }
    if (timeSlot.is_available_monday) { availableDayStrings.push('Monday'); }
    if (timeSlot.is_available_tuesday) { availableDayStrings.push('Tuesday'); }
    if (timeSlot.is_available_wednesday) { availableDayStrings.push('Wednesday'); }
    if (timeSlot.is_available_thursday) { availableDayStrings.push('Thursday'); }
    if (timeSlot.is_available_friday) { availableDayStrings.push('Friday'); }
    if (timeSlot.is_available_saturday) { availableDayStrings.push('Saturday'); }
    return availableDayStrings.join(', ');

  }

  /**
   * Remove the given schedule.
   *
   * Deleting a schedule removes its time slots automatically, and deletes its
   * future events unless they have active reservations. Any events which would
   * be left orphaned (because they have active reservations) are shown to the
   * user in a warning modal first, so they can decide whether to proceed.
   *
   * On a rolling event type the future events are deleted along with their
   * bookings, so there is usually nothing to list; the user is told how many
   * events and bookings the deletion will remove instead.
   */
  public removeSchedule(schedule: EventSchedule) {
    this.eventTypeService
      .getEventScheduleDeletionPreview(this.eventType.id, schedule.id)
      .then(preview => {
        const bookingWarning = getScheduleDeletionBookingWarning(
          'schedule',
          preview
        );

        if (preview.remaining_events.length > 0) {
          this.openOrphanedEventsWarning(
            schedule,
            preview.remaining_events,
            bookingWarning
          );
        } else if (confirm(
          bookingWarning
            ? `${bookingWarning} Are you sure you want to continue?`
            : 'Are you sure you want to remove this schedule? This action cannot be undone.'
        )) {
          this.deleteSchedule(schedule);
        }
      })
      .catch(() => {
        // The preview is advisory — if it cannot be loaded (e.g. a transient
        // server error), fall back to the plain confirmation rather than
        // blocking deletion entirely.
        if (confirm(
          'Are you sure you want to remove this schedule? This action cannot be undone.'
        )) {
          this.deleteSchedule(schedule);
        }
      });
  }

  /**
   * Warn the user that deleting the schedule will leave the given events
   * orphaned, and delete the schedule if they choose to proceed.
   *
   * The booking warning is carried into the modal rather than dropped: on a
   * rolling event type the deletion both destroys booked events and leaves a
   * benefit-granting one behind, and staff need to be told about both.
   */
  private openOrphanedEventsWarning(
    schedule: EventSchedule,
    remainingEvents: ScheduleDeletionPreviewEvent[],
    bookingWarning: string | null
  ) {
    const modalRef = this.modalService.open(
      ScheduleOrphanedEventsModalComponent,
      { size: 'lg' }
    );
    modalRef.componentInstance.scheduleName =
      this.getScheduleNameString(schedule);
    modalRef.componentInstance.remainingEvents = remainingEvents;
    modalRef.componentInstance.bookingWarning = bookingWarning || '';

    if (this.eventType.schedule_type === ScheduleType.rolling) {
      modalRef.componentInstance.remainingReasonSingular =
        ROLLING_ORPHAN_REASON_SINGULAR;
      modalRef.componentInstance.remainingReasonPlural =
        ROLLING_ORPHAN_REASON_PLURAL;
    }

    modalRef.componentInstance.confirmedCallback = () => {
      this.deleteSchedule(schedule);
    };
  }

  /**
   * Delete the given schedule via the API, removing it from the list on
   * success.
   */
  private deleteSchedule(schedule: EventSchedule) {
    this.eventTypeService
      .deleteEventSchedule(schedule)
      .then(() => {
        delete this.eventTimeSlots[schedule.id];

        const index = this.eventSchedules.indexOf(schedule);
        if (index !== -1) {
          this.eventSchedules.splice(index, 1);
        }
      })
      .catch(() => {
        this.openErrorModal(
          'Something went wrong while deleting this schedule. Please try again.'
        );
      });
  }

  private openErrorModal(message: string) {
    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Error';
    modalRef.componentInstance.messageText = message;
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
  }

  public getFilteredSchedules(): EventSchedule[] {
    if (!this.eventSchedules) { return this.eventSchedules; }

    return this.eventSchedules.filter(schedule => {
      if (this.locationId) {
        if (schedule.location_id !== this.locationId) { return false; }
      }

      if (this.hideEnded) {
        if (schedule.end_date) {
          if (moment(schedule.end_date).isBefore(moment())) { return false; }
        }
      }

      return true;
    });
  }

  public getScheduleNameString(schedule: EventSchedule): string {
    let name = schedule.name;

    if (schedule.customer_facing_name) {
      if (name) { name += ' - '; }
      name += schedule.customer_facing_name;
    }

    return name;
  }
}
